import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { LabelRequest } from './packingApi';
import { badge, btn, num, td, th } from './packingStyles';

interface Props {
  requests: LabelRequest[];
  onAccept: (request: LabelRequest) => void;
  /** Admin only: add surplus by hand / remove a hand-added one that was not accepted yet. */
  onAdd?: () => void;
  onRemove?: (request: LabelRequest) => void;
}

/**
 * "Nadwyżki": pieces above the production sheet, or added by hand by an admin (no line or week);
 * accepted into packing once their labels are printed.
 */
const PackingSurplusView: React.FC<Props> = ({ requests, onAccept, onAdd, onRemove }) => {
  const { t } = useLanguage();

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 12 }}>
        <p style={{ margin: 0, color: '#475569', fontSize: 14, flex: '1 1 320px' }}>{t('packing.surplus.hint')}</p>
        {onAdd && <button type="button" style={btn('primary')} onClick={onAdd}>+ {t('manualSurplus.add')}</button>}
      </div>
      {requests.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>{t('packing.surplus.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                {['week', 'line', 'name', 'index'].map((k) => (
                  <th key={k} style={th}>{t(`packing.col.${k}`)}</th>
                ))}
                <th style={{ ...th, textAlign: 'right' }}>{t('packing.surplus.qty')}</th>
                <th style={th}>{t('packing.surplus.status')}</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td style={td}>{r.row?.tydzien ?? '-'}</td>
                  <td style={td}>{r.row?.linia ?? '-'}</td>
                  <td style={td}>{r.row?.nazwa}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.row?.indeks}</td>
                  <td style={{ ...num, fontWeight: 800 }}>{t('packing.pcs', { n: r.quantity })}</td>
                  <td style={td}>
                    {r.is_manual && <span style={badge('#e0e7ff', '#3730a3')}>{t('manualSurplus.badge')}</span>}
                    {r.printed_at ? (
                      <span style={badge('#dcfce7', '#166534')}>{t('packing.surplus.printed')}</span>
                    ) : (
                      <span style={badge('#fef3c7', '#92400e')}>{t('packing.surplus.waitingLabels')}</span>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      style={{ ...btn('primary'), opacity: r.printed_at ? 1 : 0.5 }}
                      disabled={!r.printed_at}
                      onClick={() => onAccept(r)}
                    >
                      {t('packing.surplus.accept')}
                    </button>
                    {r.is_manual && onRemove && (
                      <button type="button" style={{ ...btn('danger'), marginLeft: 6 }} onClick={() => onRemove(r)}>
                        {t('manualSurplus.remove')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default PackingSurplusView;
